/**
 * Garderie › Facturation mensuelle : le brouillon d'un mois d'une école, puis
 * son émission.
 *
 * L'administration choisit l'école et le mois, pose les paramètres du mois
 * (période couverte, jours d'école du midi, période offerte), et la base
 * calcule le brouillon (`garderie_brouillon`, jumeau SQL du moteur
 * `lib/garderie/calcul.ts`) : par famille payeuse, les enfants, les lignes
 * (formule, à la carte, midi), les totaux, et ce qui demande un regard —
 * les midis « à confirmer » (enfant sans formule ni présence : est-il encore
 * là ?) et les avertissements (parts des payeurs, sortis, présences hors jours).
 *
 * Un midi « à confirmer » se règle ici même : « rentre le midi » sur la fiche
 * de l'enfant, puis recalcul. Quand le brouillon est lu, l'administration émet
 * le mois (`garderie_emettre_mois`, migration 20261101220000) : une facture
 * `ATL-` par famille, l'annotation « documents d'inscription non reçus » pour
 * un enfant sans abonnement, le mois fermé aux présences. Les factures émises
 * se listent et s'exportent ; le journal des ventes les prend à son passage
 * (geste comptable distinct, sur accord).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Download, FileText, Loader2, Lock, RefreshCw, Save, Send, Utensils } from 'lucide-react';
import {
  ANNOTATION_PAR_DEFAUT,
  LIBELLES_AVERTISSEMENTS,
  LIBELLES_TYPES,
  brouillonEnCsv,
  chercherLesFamilles,
  compterLesAvertissements,
  compterLesJoursDeMidi,
  dateEnClair,
  enEuros,
  enfantsAConfirmer,
  facturesEnCsv,
  famillesAVoir,
  famillesFacturables,
  messageDeLaBase,
  moisDeLAnnee,
  moisEnClair,
  periodeParDefaut,
  totalDesFactures,
  type Brouillon,
  type FactureEmise,
  type FamilleDuBrouillon,
  type MoisDeGarderie,
  type ResultatDeLEmission,
} from '../../../lib/garderie/brouillon';
import {
  calculerLeBrouillon,
  emettreLeMois,
  enregistrerLeMois,
  lireLesEcolesAvecCatalogue,
  lireLesFactures,
  lireLesMois,
  poserRentreLeMidi,
  type EcoleAvecCatalogue,
} from '../../../lib/garderie/brouillon.supabase';

interface Formulaire {
  du: string;
  au: string;
  jours: string;
  offertDu: string;
  offertAu: string;
}

const moisCourant = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const formulaireDepuis = (m: MoisDeGarderie): Formulaire => ({
  du: m.periode_du,
  au: m.periode_au,
  jours: String(m.jours_ecole_midi),
  offertDu: m.offert_du ?? '',
  offertAu: m.offert_au ?? '',
});

const formulaireParDefaut = (mois: string, ecole: EcoleAvecCatalogue): Formulaire => {
  const periode = periodeParDefaut(mois, ecole.debut, ecole.fin);
  return { du: periode.du, au: periode.au, jours: String(compterLesJoursDeMidi(periode.du, periode.au)), offertDu: '', offertAu: '' };
};

export default function AdminGarderieFacturationPage() {
  const [ecoles, setEcoles] = useState<EcoleAvecCatalogue[]>([]);
  const [ecoleId, setEcoleId] = useState('');
  const [mois, setMois] = useState('');
  const [moisConnus, setMoisConnus] = useState<MoisDeGarderie[]>([]);
  const [f, setF] = useState<Formulaire>({ du: '', au: '', jours: '0', offertDu: '', offertAu: '' });
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null);
  const [chargement, setChargement] = useState(true);
  const [calcul, setCalcul] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [filtre, setFiltre] = useState<'toutes' | 'a-voir'>('toutes');
  const [recherche, setRecherche] = useState('');
  const [ouvertes, setOuvertes] = useState<Set<string>>(new Set());
  /* L'émission. */
  const [annotation, setAnnotation] = useState(ANNOTATION_PAR_DEFAUT);
  const [assumer, setAssumer] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [emission, setEmission] = useState(false);
  const [resultat, setResultat] = useState<ResultatDeLEmission | null>(null);
  const [factures, setFactures] = useState<FactureEmise[] | null>(null);
  const [chargementFactures, setChargementFactures] = useState(false);
  const [midiEnCours, setMidiEnCours] = useState<Set<string>>(new Set());

  const ecole = useMemo(() => ecoles.find((e) => e.centerId === ecoleId) ?? null, [ecoles, ecoleId]);
  const moisEnregistre = useMemo(() => moisConnus.find((m) => m.mois === mois) ?? null, [moisConnus, mois]);
  const facture = moisEnregistre?.etat === 'facture';

  useEffect(() => {
    void (async () => {
      try {
        const lues = await lireLesEcolesAvecCatalogue();
        setEcoles(lues);
        if (lues.length) setEcoleId((id) => id || lues[0].centerId);
      } catch (e) {
        setErreur(messageDeLaBase(e));
      } finally {
        setChargement(false);
      }
    })();
  }, []);

  const relireLesMois = useCallback(async (centerId: string) => {
    const lus = await lireLesMois(centerId);
    setMoisConnus(lus);
    return lus;
  }, []);

  useEffect(() => {
    if (!ecole) return;
    const liste = moisDeLAnnee(ecole.anneeScolaire);
    const courant = moisCourant();
    setMois((m) => (m && liste.includes(m) ? m : liste.includes(courant) ? courant : liste[0]));
    setBrouillon(null);
    relireLesMois(ecole.centerId).catch((e) => setErreur(messageDeLaBase(e)));
  }, [ecole, relireLesMois]);

  useEffect(() => {
    if (!ecole || !mois) return;
    setBrouillon(null);
    setResultat(null);
    setConfirmation(false);
    setAssumer(false);
    setF(moisEnregistre ? formulaireDepuis(moisEnregistre) : formulaireParDefaut(mois, ecole));
  }, [ecole, mois, moisEnregistre]);

  /* Un mois facturé : ses factures. */
  useEffect(() => {
    if (!ecole || !mois || !facture) {
      setFactures(null);
      return;
    }
    let annule = false;
    setChargementFactures(true);
    lireLesFactures(ecole.centerId, mois)
      .then((lues) => {
        if (!annule) setFactures(lues);
      })
      .catch((e) => {
        if (!annule) setErreur(messageDeLaBase(e));
      })
      .finally(() => {
        if (!annule) setChargementFactures(false);
      });
    return () => {
      annule = true;
    };
  }, [ecole, mois, facture]);

  const proposerLesJours = () => setF((x) => ({ ...x, jours: String(compterLesJoursDeMidi(x.du, x.au)) }));

  const enregistrerEtCalculer = useCallback(async () => {
    if (!ecole || !mois) return;
    setCalcul(true);
    setErreur(null);
    setConfirmation(false);
    try {
      if (!facture) {
        const jours = Number(f.jours);
        if (!f.du || !f.au || f.du > f.au) throw new Error('La période est incomplète ou à l’envers.');
        if (!Number.isInteger(jours) || jours < 0 || jours > 31) throw new Error('Les jours d’école du midi : un entier de 0 à 31.');
        if ((f.offertDu === '') !== (f.offertAu === '')) throw new Error('La période offerte a besoin d’un début et d’une fin.');
        const enregistre = await enregistrerLeMois({
          center_id: ecole.centerId,
          mois,
          periode_du: f.du,
          periode_au: f.au,
          jours_ecole_midi: jours,
          offert_du: f.offertDu || null,
          offert_au: f.offertAu || null,
        });
        setMoisConnus((liste) => [...liste.filter((m) => m.mois !== mois), enregistre].sort((a, b) => a.mois.localeCompare(b.mois)));
      }
      setBrouillon(await calculerLeBrouillon(ecole.centerId, mois));
      setOuvertes(new Set());
    } catch (e) {
      setErreur(messageDeLaBase(e));
    } finally {
      setCalcul(false);
    }
  }, [ecole, mois, f, facture]);

  const rentreLeMidi = useCallback(
    async (enfantId: string) => {
      if (!ecole || !mois || facture) return;
      setMidiEnCours((s) => new Set(s).add(enfantId));
      setErreur(null);
      try {
        await poserRentreLeMidi(enfantId, true);
        setBrouillon(await calculerLeBrouillon(ecole.centerId, mois));
      } catch (e) {
        setErreur(messageDeLaBase(e));
      } finally {
        setMidiEnCours((s) => {
          const n = new Set(s);
          n.delete(enfantId);
          return n;
        });
      }
    },
    [ecole, mois, facture],
  );

  const emettre = useCallback(async () => {
    if (!ecole || !mois || !brouillon) return;
    setEmission(true);
    setErreur(null);
    try {
      const r = await emettreLeMois(ecole.centerId, mois, annotation.trim() || ANNOTATION_PAR_DEFAUT, assumer);
      setResultat(r);
      setConfirmation(false);
      await relireLesMois(ecole.centerId);
    } catch (e) {
      setErreur(messageDeLaBase(e));
    } finally {
      setEmission(false);
    }
  }, [ecole, mois, brouillon, annotation, assumer, relireLesMois]);

  const exporter = () => {
    if (!brouillon) return;
    telecharger(brouillonEnCsv(brouillon), `brouillon-garderie-${brouillon.ecole.tag}-${brouillon.mois.mois.slice(0, 7)}.csv`);
  };

  const exporterLesFactures = () => {
    if (!factures || !ecole || !mois) return;
    telecharger(facturesEnCsv(factures, ecole.tag, mois), `factures-garderie-${ecole.tag}-${mois.slice(0, 7)}.csv`);
  };

  const famillesAffichees = useMemo(() => {
    if (!brouillon) return [];
    const base = filtre === 'a-voir' ? famillesAVoir(brouillon) : brouillon.familles;
    return chercherLesFamilles(base, recherche);
  }, [brouillon, filtre, recherche]);

  const aConfirmer = useMemo(() => (brouillon ? enfantsAConfirmer(brouillon) : []), [brouillon]);
  const facturables = useMemo(() => (brouillon ? famillesFacturables(brouillon) : []), [brouillon]);
  const totauxFactures = useMemo(() => (factures ? totalDesFactures(factures) : null), [factures]);

  const basculer = (id: string) =>
    setOuvertes((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  if (chargement) return <Loader2 className="mx-auto mt-16 h-8 w-8 animate-spin text-primary-500" />;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">Garderie — facturation mensuelle</h1>
        <p className="mt-1 text-sm text-gray-600">
          Le brouillon d’un mois : ce que chaque famille doit, calculé par la base d’après les abonnements, les présences pointées et le
          catalogue de l’école. Puis son émission : une facture par famille, le mois fermé.
        </p>
      </header>

      {erreur && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{erreur}</span>
        </div>
      )}

      {ecoles.length === 0 ? (
        <p className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Aucune école n’a de catalogue de garderie : la migration de la garderie et son catalogue 2026-27 ne sont pas en place.
        </p>
      ) : (
        <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="text-sm">
              <span className="block font-medium text-gray-700">École</span>
              <select value={ecoleId} onChange={(e) => setEcoleId(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2">
                {ecoles.map((e) => (
                  <option key={e.centerId} value={e.centerId}>
                    {e.nom} — {e.anneeScolaire}-{e.anneeScolaire + 1}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="block font-medium text-gray-700">Mois</span>
              <select value={mois} onChange={(e) => setMois(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2">
                {ecole &&
                  moisDeLAnnee(ecole.anneeScolaire).map((m) => {
                    const connu = moisConnus.find((x) => x.mois === m);
                    return (
                      <option key={m} value={m}>
                        {moisEnClair(m)}
                        {connu ? ` · ${connu.etat === 'facture' ? 'facturé' : connu.etat}` : ''}
                      </option>
                    );
                  })}
              </select>
            </label>
            <label className="text-sm">
              <span className="block font-medium text-gray-700">Période du</span>
              <input type="date" value={f.du} disabled={facture} onChange={(e) => setF({ ...f, du: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50" />
            </label>
            <label className="text-sm">
              <span className="block font-medium text-gray-700">au</span>
              <input type="date" value={f.au} disabled={facture} onChange={(e) => setF({ ...f, au: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50" />
            </label>
            <label className="text-sm">
              <span className="block font-medium text-gray-700">Jours d’école du midi</span>
              <div className="mt-1 flex gap-2">
                <input type="number" min={0} max={31} value={f.jours} disabled={facture} onChange={(e) => setF({ ...f, jours: e.target.value })} className="w-24 rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50" />
                <button type="button" onClick={proposerLesJours} disabled={facture} className="rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40" title="Lundi, mardi, jeudi, vendredi de la période">
                  Proposer : {compterLesJoursDeMidi(f.du, f.au)}
                </button>
              </div>
            </label>
            <label className="text-sm">
              <span className="block font-medium text-gray-700">Offert du</span>
              <input type="date" value={f.offertDu} disabled={facture} onChange={(e) => setF({ ...f, offertDu: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50" />
            </label>
            <label className="text-sm">
              <span className="block font-medium text-gray-700">au</span>
              <input type="date" value={f.offertAu} disabled={facture} onChange={(e) => setF({ ...f, offertAu: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50" />
            </label>
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => void enregistrerEtCalculer()}
                disabled={calcul || !ecole || !mois}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 font-semibold text-white disabled:opacity-40"
              >
                {calcul ? <Loader2 className="h-4 w-4 animate-spin" /> : brouillon ? <RefreshCw className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                {facture ? 'Relire le mois facturé' : brouillon ? 'Recalculer' : 'Enregistrer et calculer'}
              </button>
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            La période offerte ne facture aucune présence à la carte ; les formules restent dues en entier. Les jours d’école du midi
            comptent lundi, mardi, jeudi et vendredi : retirez les congés et journées pédagogiques à la main.
            {moisEnregistre?.etat === 'facture' && moisEnregistre.facture_le && (
              <> Ce mois a été facturé le {new Date(moisEnregistre.facture_le).toLocaleDateString('fr-BE')} : ses paramètres ne changent plus.</>
            )}
          </p>
        </section>
      )}

      {resultat && (
        <section className="flex items-start gap-3 rounded-2xl border border-green-200 bg-green-50 p-5 text-sm text-green-900">
          <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-700" />
          <div>
            <p className="font-semibold">
              {resultat.factures} facture(s) émise(s) pour {enEuros(resultat.montant_cents)}
              {resultat.premier_numero && (
                <>
                  {' '}
                  — de {resultat.premier_numero} à {resultat.dernier_numero}
                </>
              )}
              .
            </p>
            <p className="mt-1">
              {resultat.avec_annotation} avec l’annotation « documents d’inscription non reçus » · {resultat.sans_email} sans adresse e-mail (PDF à remettre par
              l’école) · {enEuros(resultat.a_confirmer_cents)} de midis « à confirmer » facturés · {resultat.avertissements} avertissement(s) assumé(s). Le mois est
              fermé aux présences. Le journal des ventes les prendra à son prochain passage.
            </p>
          </div>
        </section>
      )}

      {facture && (
        <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="flex items-center gap-2 font-semibold text-gray-900">
              <FileText className="h-5 w-5 text-gray-500" />
              Factures émises
              {totauxFactures && (
                <span className="text-sm font-normal text-gray-500">
                  {totauxFactures.nombre} facture(s), {enEuros(totauxFactures.montant_cents)} · {totauxFactures.sans_email} sans e-mail · {totauxFactures.sans_abonnement} avec annotation
                </span>
              )}
            </h2>
            <button type="button" onClick={exporterLesFactures} disabled={!factures?.length} className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-40">
              <Download className="h-4 w-4" /> CSV des factures
            </button>
          </div>
          {chargementFactures ? (
            <Loader2 className="mx-auto mt-6 h-6 w-6 animate-spin text-primary-500" />
          ) : factures && factures.length > 0 ? (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500">
                    <th className="py-1 pr-2">Numéro</th>
                    <th className="py-1 pr-2">Famille</th>
                    <th className="py-1 pr-2">E-mail</th>
                    <th className="py-1 pr-2">Communication</th>
                    <th className="py-1 pr-2 text-right">Montant</th>
                    <th className="py-1 pr-2">Échéance</th>
                    <th className="py-1 pr-2">Document</th>
                  </tr>
                </thead>
                <tbody>
                  {factures.map((x) => (
                    <tr key={x.id} className="border-t border-gray-100">
                      <td className="py-1 pr-2 font-mono text-xs">{x.numero}</td>
                      <td className="py-1 pr-2">
                        <span className="font-medium text-gray-900">
                          {x.nom} {x.prenom ?? ''}
                        </span>
                        {x.statut !== 'plein' && <span className="ml-2 rounded-full bg-blue-50 px-2 text-xs text-blue-700">{x.statut}</span>}
                        {x.sans_abonnement && (
                          <span className="ml-2 rounded-full bg-amber-50 px-2 text-xs text-amber-700" title={x.annotation ?? ''}>
                            sans abonnement
                          </span>
                        )}
                        {x.a_confirmer_cents > 0 && <span className="ml-2 rounded-full bg-amber-50 px-2 text-xs text-amber-700">midi à confirmer {enEuros(x.a_confirmer_cents)}</span>}
                        <span className="ml-2 text-xs text-gray-500">
                          {x.enfants} enfant(s), {x.lignes} ligne(s)
                        </span>
                      </td>
                      <td className="py-1 pr-2 text-xs text-gray-600">{x.email ?? <span className="text-amber-700">sans e-mail</span>}</td>
                      <td className="py-1 pr-2 font-mono text-xs">{x.communication_imprimee}</td>
                      <td className="py-1 pr-2 text-right font-medium tabular-nums">{enEuros(x.montant_cents)}</td>
                      <td className="py-1 pr-2 text-xs text-gray-600">{x.echeance ? dateEnClair(x.echeance.slice(0, 10)) : ''}</td>
                      <td className="py-1 pr-2 text-xs text-gray-600">
                        {x.pdf_url ? (
                          <a href={x.pdf_url} target="_blank" rel="noreferrer" className="text-primary-700 underline">
                            PDF
                          </a>
                        ) : (
                          'pas encore'
                        )}
                        {x.courriel_envoye_le && <span className="ml-1">· envoyé le {dateEnClair(x.courriel_envoye_le.slice(0, 10))}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-3 text-sm text-gray-500">Aucune facture pour ce mois.</p>
          )}
        </section>
      )}

      {brouillon && (
        <>
          <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Carte titre="Total du mois" valeur={enEuros(brouillon.totaux.total_cents)} sous={`dont ${enEuros(brouillon.totaux.total_cents - brouillon.totaux.a_confirmer_cents)} sûrs`} />
            <Carte titre="À confirmer" valeur={enEuros(brouillon.totaux.a_confirmer_cents)} sous="midis sans formule ni présence" accent={brouillon.totaux.a_confirmer_cents > 0 ? 'amber' : undefined} />
            <Carte titre="Familles" valeur={String(brouillon.totaux.familles)} sous={`${brouillon.totaux.enfants} enfants, ${brouillon.totaux.lignes} lignes`} />
            <Carte titre="Avertissements" valeur={String(brouillon.totaux.avertissements)} sous="à lire avant d’émettre" accent={brouillon.totaux.avertissements > 0 ? 'red' : undefined} />
            <Carte titre="Période" valeur={`${dateEnClair(brouillon.mois.periode_du)} → ${dateEnClair(brouillon.mois.periode_au)}`} sous={`${brouillon.mois.jours_ecole_midi} jours de midi${brouillon.mois.offert_du ? `, offert du ${dateEnClair(brouillon.mois.offert_du)} au ${dateEnClair(brouillon.mois.offert_au!)}` : ''}`} petite />
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
              <h2 className="font-semibold text-gray-900">Par formule et par plage</h2>
              <table className="mt-3 w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500">
                    <th className="py-1">Type</th>
                    <th className="py-1">Code</th>
                    <th className="py-1 text-right">Enfants</th>
                    <th className="py-1 text-right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {brouillon.totaux.par_code.map((c) => (
                    <tr key={`${c.type}-${c.code}`} className="border-t border-gray-100">
                      <td className="py-1 text-gray-600">{LIBELLES_TYPES[c.type]}</td>
                      <td className="py-1 font-medium">{c.code}</td>
                      <td className="py-1 text-right">{c.enfants}</td>
                      <td className="py-1 text-right tabular-nums">{enEuros(c.montant_cents)}</td>
                    </tr>
                  ))}
                  {brouillon.totaux.par_code.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-3 text-center text-gray-500">
                        Rien à facturer ce mois.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
              <h2 className="font-semibold text-gray-900">Avertissements</h2>
              {brouillon.avertissements.length === 0 ? (
                <p className="mt-3 text-sm text-gray-500">Aucun.</p>
              ) : (
                <ul className="mt-3 space-y-1 text-sm">
                  {compterLesAvertissements(brouillon).map((a) => (
                    <li key={a.code} className="flex items-start justify-between gap-3">
                      <span className="text-gray-700">{a.libelle}</span>
                      <span className="rounded-full bg-red-50 px-2 text-xs font-semibold text-red-700">{a.nombre}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-3 text-xs text-gray-500">Le détail est sur chaque famille, ci-dessous. Les midis « à confirmer » se règlent sur la fiche de l’enfant : « rentre le midi » ou date de sortie.</p>
            </div>
          </section>

          {!facture && aConfirmer.length > 0 && (
            <section className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5">
              <h2 className="flex items-center gap-2 font-semibold text-amber-900">
                <Utensils className="h-5 w-5" />
                Midis à confirmer ({aConfirmer.length} enfant{aConfirmer.length > 1 ? 's' : ''})
              </h2>
              <p className="mt-1 text-sm text-amber-900">
                Sans formule et sans présence ce mois : l’enfant reste-t-il le midi ? S’il rentre à la maison, posez-le sur sa fiche ; le brouillon se
                recalcule. Sinon, le midi se facture à la carte, avec l’annotation.
              </p>
              <ul className="mt-3 divide-y divide-amber-200/60">
                {aConfirmer.map((e) => (
                  <li key={e.enfant_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <span>
                      <span className="font-medium text-gray-900">{e.enfant}</span>
                      <span className="ml-2 text-gray-600">famille {e.famille}</span>
                      <span className="ml-2 tabular-nums text-amber-800">{enEuros(e.montant_cents)}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => void rentreLeMidi(e.enfant_id)}
                      disabled={midiEnCours.has(e.enfant_id) || calcul}
                      className="flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-40"
                    >
                      {midiEnCours.has(e.enfant_id) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Utensils className="h-3.5 w-3.5" />}
                      Rentre le midi
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {!facture && (
            <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-primary-200">
              <h2 className="flex items-center gap-2 font-semibold text-gray-900">
                <Send className="h-5 w-5 text-primary-600" />
                Émettre les factures de {moisEnClair(brouillon.mois.mois)}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {facturables.length} famille{facturables.length > 1 ? 's' : ''} recevront une facture, pour {enEuros(brouillon.totaux.total_cents)}
                {brouillon.totaux.a_confirmer_cents > 0 && <> dont {enEuros(brouillon.totaux.a_confirmer_cents)} de midis à confirmer</>}. Le brouillon est
                recalculé à l’instant de l’émission et gelé ; le mois se ferme aux présences ; une facture émise ne se modifie plus (note de crédit).
              </p>
              <label className="mt-4 block text-sm">
                <span className="font-medium text-gray-700">Annotation des factures dont un enfant est facturé sans abonnement</span>
                <textarea value={annotation} onChange={(e) => setAnnotation(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                <span className="mt-1 block text-xs text-gray-500">Documents d’inscription non reçus en début d’année, régularisation aux permanences (décision du 01/11/2026).</span>
              </label>
              {brouillon.totaux.avertissements > 0 && (
                <label className="mt-3 flex items-start gap-2 text-sm text-red-900">
                  <input type="checkbox" checked={assumer} onChange={(e) => setAssumer(e.target.checked)} className="mt-0.5 rounded border-gray-300" />
                  <span>
                    J’ai lu les {brouillon.totaux.avertissements} avertissement(s) et j’émets malgré eux (un enfant aux parts fausses n’est pas facturé ; un sorti encore
                    abonné l’est).
                  </span>
                </label>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {!confirmation ? (
                  <button
                    type="button"
                    onClick={() => setConfirmation(true)}
                    disabled={emission || calcul || facturables.length === 0 || (brouillon.totaux.avertissements > 0 && !assumer)}
                    className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 font-semibold text-white disabled:opacity-40"
                  >
                    <Send className="h-4 w-4" /> Émettre les factures
                  </button>
                ) : (
                  <>
                    <span className="text-sm font-medium text-gray-900">
                      Confirmer l’émission de {facturables.length} facture(s) pour {enEuros(brouillon.totaux.total_cents)} ? Le mois sera fermé.
                    </span>
                    <button type="button" onClick={() => void emettre()} disabled={emission} className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 font-semibold text-white disabled:opacity-40">
                      {emission ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Oui, émettre et fermer le mois
                    </button>
                    <button type="button" onClick={() => setConfirmation(false)} disabled={emission} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">
                      Non
                    </button>
                  </>
                )}
                {facturables.length === 0 && <span className="text-sm text-gray-500">Rien à facturer : aucune famille au total positif.</span>}
              </div>
            </section>
          )}

          <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="font-semibold text-gray-900">
                Familles <span className="text-sm font-normal text-gray-500">({famillesAffichees.length} sur {brouillon.familles.length})</span>
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <input type="search" value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Nom, e-mail, enfant…" className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
                <select value={filtre} onChange={(e) => setFiltre(e.target.value as 'toutes' | 'a-voir')} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
                  <option value="toutes">Toutes</option>
                  <option value="a-voir">À voir (à confirmer ou avertissement)</option>
                </select>
                <button type="button" onClick={exporter} className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
                  <Download className="h-4 w-4" /> CSV
                </button>
              </div>
            </div>
            <ul className="mt-4 divide-y divide-gray-100">
              {famillesAffichees.map((fam) => (
                <LigneFamille key={fam.famille_id} famille={fam} ouverte={ouvertes.has(fam.famille_id)} onBasculer={() => basculer(fam.famille_id)} />
              ))}
              {famillesAffichees.length === 0 && <li className="py-6 text-center text-sm text-gray-500">Aucune famille ne correspond.</li>}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}

function telecharger(texte: string, nom: string) {
  const blob = new Blob(['﻿' + texte], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = nom;
  a.click();
  URL.revokeObjectURL(a.href);
}

function Carte({ titre, valeur, sous, accent, petite }: { titre: string; valeur: string; sous?: string; accent?: 'amber' | 'red'; petite?: boolean }) {
  const couleur = accent === 'amber' ? 'text-amber-700' : accent === 'red' ? 'text-red-700' : 'text-gray-900';
  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
      <p className="text-xs uppercase tracking-wide text-gray-500">{titre}</p>
      <p className={`mt-1 font-bold tabular-nums ${petite ? 'text-base' : 'text-2xl'} ${couleur}`}>{valeur}</p>
      {sous && <p className="mt-1 text-xs text-gray-500">{sous}</p>}
    </div>
  );
}

function LigneFamille({ famille, ouverte, onBasculer }: { famille: FamilleDuBrouillon; ouverte: boolean; onBasculer: () => void }) {
  return (
    <li className="py-2">
      <button type="button" onClick={onBasculer} className="flex w-full items-center gap-3 text-left">
        {ouverte ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" /> : <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />}
        <span className="min-w-0 flex-1">
          <span className="font-medium text-gray-900">
            {famille.nom} {famille.prenom ?? ''}
          </span>
          <span className="ml-2 text-xs text-gray-500">{famille.email ?? 'sans e-mail'}</span>
          {famille.statut !== 'plein' && <span className="ml-2 rounded-full bg-blue-50 px-2 text-xs text-blue-700">{famille.statut}</span>}
          {famille.a_confirmer_cents > 0 && <span className="ml-2 rounded-full bg-amber-50 px-2 text-xs text-amber-700">à confirmer {enEuros(famille.a_confirmer_cents)}</span>}
          {famille.avertissements.length > 0 && <span className="ml-2 rounded-full bg-red-50 px-2 text-xs text-red-700">{famille.avertissements.length} avertissement(s)</span>}
        </span>
        <span className="font-semibold tabular-nums text-gray-900">{enEuros(famille.total_cents)}</span>
      </button>
      {ouverte && (
        <div className="ml-7 mt-2 space-y-3">
          {famille.enfants.map((e) => (
            <div key={e.enfant_id}>
              <p className="text-sm font-medium text-gray-800">
                {e.nom} {e.prenom} <span className="font-normal text-gray-500">— {enEuros(e.total_cents)}</span>
              </p>
              <table className="mt-1 w-full text-sm">
                <tbody>
                  {e.lignes.map((l, i) => (
                    <tr key={i} className={`border-t border-gray-100 ${l.a_confirmer ? 'bg-amber-50/60' : ''}`}>
                      <td className="py-1 pr-2 text-gray-600">{LIBELLES_TYPES[l.type]}</td>
                      <td className="py-1 pr-2">
                        {l.libelle}
                        {l.detail && <span className="ml-1 text-xs text-amber-700">· {l.detail}</span>}
                        {l.dates.length > 0 && <span className="ml-1 text-xs text-gray-500">({l.dates.map(dateEnClair).join(', ')})</span>}
                      </td>
                      <td className="py-1 pr-2 text-right tabular-nums text-gray-600">
                        {l.quantite} × {enEuros(l.prix_unitaire_cents)}
                      </td>
                      <td className="py-1 pr-2 text-right text-xs text-gray-500">{l.part_pct < 100 ? `${l.part_pct} %` : ''}</td>
                      <td className="py-1 text-right font-medium tabular-nums">{enEuros(l.montant_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {famille.avertissements.length > 0 && (
            <ul className="space-y-1 text-sm text-red-800">
              {famille.avertissements.map((a, i) => (
                <li key={i} className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>
                    <span className="font-medium">{a.enfant}</span> — {(LIBELLES_AVERTISSEMENTS as Record<string, string>)[a.code] ?? a.libelle}
                    {a.detail && <span className="text-red-700"> ({a.detail})</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}
